import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import type {
  AdhesionSummaryResponse,
  AdhesionSummaryResponseItem,
  ProponenteSubestipulanteItemResponse,
  ProponenteSubestipulanteListResponse,
  ValidacaoPAProponenteItemResponse,
  ValidacaoPAProponenteListResponse,
} from '../../types/vida/adhesionProposal/adhesionProposalType';

export interface UseVidaAdhesionProposalReturn {
  isLoading: boolean;
  error: string | null;
  summary: AdhesionSummaryResponse | null;
  proposals: ValidacaoPAProponenteItemResponse[];
  proponentProposals: ProponenteSubestipulanteItemResponse[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  clearError: () => void;
  loadAdhesionProposalData: (
    numeroApolice: string,
    dtInicio: string,
    dtFim: string,
    identificadorSubestipulante?: string,
    page?: number,
    size?: number
  ) => Promise<void>;
  loadAdhesionProposalByProponentData: (
    identSubestipulante: number,
    nmProponente?: string,
    cdCpf?: string,
    cdCtrlCpf?: string,
    cdMatricula?: string,
    page?: number,
    size?: number
  ) => Promise<void>;
}

const processSummaryFromValidacaoList = (
  data: ValidacaoPAProponenteListResponse
): AdhesionSummaryResponse => {
  const items = data.pagina.content;
  const total = data.pagina.totalElements;

  const statusMap = new Map<number, number>();
  items.forEach((item) => {
    const count = statusMap.get(item.status) || 0;
    statusMap.set(item.status, count + 1);
  });

  const summary: AdhesionSummaryResponseItem[] = Array.from(statusMap.entries()).map(
    ([status, count]) => ({
      status: status.toString(),
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      total,
    })
  );

  return summary;
};

const processSummaryFromProponentList = (
  data: ProponenteSubestipulanteListResponse
): AdhesionSummaryResponse => {
  const items = data.pagina.content;
  const total = data.pagina.totalElements;

  const statusMap = new Map<number, number>();
  items.forEach((item) => {
    const count = statusMap.get(item.statusProponente) || 0;
    statusMap.set(item.statusProponente, count + 1);
  });

  const summary: AdhesionSummaryResponseItem[] = Array.from(statusMap.entries()).map(
    ([status, count]) => ({
      status: status.toString(),
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      total,
    })
  );

  return summary;
};

export const useVidaAdhesionProposal = (): UseVidaAdhesionProposalReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AdhesionSummaryResponse | null>(null);
  const [proposals, setProposals] = useState<ValidacaoPAProponenteItemResponse[]>([]);
  const [proponentProposals, setProponentProposals] = useState<ProponenteSubestipulanteItemResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(6);

  const loadAdhesionProposalData = useCallback(async (
    numeroApolice: string,
    dtInicio: string,
    dtFim: string,
    identificadorSubestipulante?: string,
    page?: number,
    size?: number
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const [listData, summaryData] = await Promise.all([
        apiService.adhesionProposal.getPAProponentValidationList(
          numeroApolice,
          dtInicio,
          dtFim,
          identificadorSubestipulante,
          page,
          size
        ),
        apiService.adhesionProposal.getPAProponentValidationList(
          numeroApolice,
          dtInicio,
          dtFim,
          identificadorSubestipulante,
          0,
          1000
        ),
      ]);

      setSummary(processSummaryFromValidacaoList(summaryData));
      setProposals(listData.pagina.content);
      setProponentProposals([]);
      setCurrentPage(listData.pagina.pageNumber);
      setTotalPages(listData.pagina.totalPages);
      setTotalElements(listData.pagina.totalElements);
      setPageSize(listData.pagina.pageSize);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados de proposta de adesão';
      setError(errorMessage);
      setSummary(null);
      setProposals([]);
      setProponentProposals([]);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAdhesionProposalByProponentData = useCallback(async (
    identSubestipulante: number,
    nmProponente?: string,
    cdCpf?: string,
    cdCtrlCpf?: string,
    cdMatricula?: string,
    page?: number,
    size?: number
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const [listData, summaryData] = await Promise.all([
        apiService.adhesionProposal.getSubestipulanteProponentList(
          identSubestipulante,
          nmProponente,
          cdCpf,
          cdCtrlCpf,
          cdMatricula !== undefined && cdMatricula !== '' ? Number(cdMatricula) : undefined,
          page,
          size
        ),
        apiService.adhesionProposal.getSubestipulanteProponentList(
          identSubestipulante,
          nmProponente,
          cdCpf,
          cdCtrlCpf,
          cdMatricula !== undefined && cdMatricula !== '' ? Number(cdMatricula) : undefined,
          0,
          1000
        ),
      ]);

      setSummary(processSummaryFromProponentList(summaryData));
      setProponentProposals(listData.pagina.content);
      setProposals([]);
      setCurrentPage(listData.pagina.pageNumber);
      setTotalPages(listData.pagina.totalPages);
      setTotalElements(listData.pagina.totalElements);
      setPageSize(listData.pagina.pageSize);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados de proposta de adesão';
      setError(errorMessage);
      setSummary(null);
      setProposals([]);
      setProponentProposals([]);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    summary,
    proposals,
    proponentProposals,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    clearError,
    loadAdhesionProposalData,
    loadAdhesionProposalByProponentData,
  };
};
